import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  AgentCredentialMessageResponseDto,
  AgentCredentialsOverviewResponseDto,
  ApiKeyCheckResponseDto,
  McpOAuthStartResponseDto,
  ProviderApiKeyResponseDto,
} from '../models/agent-credentials.dto';

/**
 * The credentials an organization lends the agent.
 *
 * Scope is read from a different place by each half of the API — path and query for the
 * key and the grant, body for starting a consent — so `scopeParams` exists to keep the
 * one optional argument from being spelled three ways at the call sites.
 */
@Injectable({
  providedIn: 'root',
})
export class AgentCredentialsService {
  private readonly apiUrl = `${environment.apiHost}/api`;

  constructor(private readonly http: HttpClient) {}

  /** Narrows a request to one project. Omitted entirely for an organization-wide one. */
  private scopeParams(projectId?: string): { params?: Record<string, string> } {
    return projectId ? { params: { project_id: projectId } } : {};
  }

  /**
   * The only read: every credential the organization holds, or — with a project — that
   * project's alone.
   *
   * The two differ in who may ask. Listing the lot enumerates projects and takes
   * organization permission; narrowing to one takes only that project's, so a project
   * administrator can open the same panel inside their project.
   */
  public getOverview(organizationId: string, projectId?: string): Observable<AgentCredentialsOverviewResponseDto> {
    return this.http.get<AgentCredentialsOverviewResponseDto>(
      `${this.apiUrl}/agent-credentials/${organizationId}/`,
      this.scopeParams(projectId),
    );
  }

  // --- api keys -------------------------------------------------------------

  /**
   * Stores a key, replacing whatever was there for the same scope.
   *
   * The backend checks it with the provider first, so this can fail for two different
   * reasons: 400 means the provider rejected the key, 503 means it could not be reached
   * and nothing was changed. Both leave any previous key untouched.
   */
  public saveApiKey(
    provider: string,
    organizationId: string,
    key: string,
    projectId?: string,
  ): Observable<ProviderApiKeyResponseDto> {
    return this.http.put<ProviderApiKeyResponseDto>(
      `${this.apiUrl}/agent-credentials/keys/${provider}/${organizationId}/`,
      { key },
      this.scopeParams(projectId),
    );
  }

  /**
   * Asks the provider whether the stored key still works.
   *
   * The only test possible once a key is saved, since nothing reads one back — and worth
   * having because a key valid when it was pasted can be revoked at the provider without
   * anything here noticing. A failed check never removes the stored key.
   */
  public checkApiKey(provider: string, organizationId: string, projectId?: string): Observable<ApiKeyCheckResponseDto> {
    return this.http.post<ApiKeyCheckResponseDto>(
      `${this.apiUrl}/agent-credentials/keys/${provider}/${organizationId}/check/`,
      {},
      this.scopeParams(projectId),
    );
  }

  public removeApiKey(
    provider: string,
    organizationId: string,
    projectId?: string,
  ): Observable<AgentCredentialMessageResponseDto> {
    return this.http.delete<AgentCredentialMessageResponseDto>(
      `${this.apiUrl}/agent-credentials/keys/${provider}/${organizationId}/`,
      this.scopeParams(projectId),
    );
  }

  // --- mcp grants -----------------------------------------------------------

  /**
   * Returns the URL to send the user to; it does not redirect.
   *
   * POST, and the scope travels in the body, because starting a consent is not a safe
   * request: it registers a client with the provider and records a pending consent.
   */
  public startMcpOAuth(
    provider: string,
    organizationId: string,
    projectId?: string,
  ): Observable<McpOAuthStartResponseDto> {
    const body: Record<string, string> = { organization_id: organizationId };
    if (projectId) {
      body['project_id'] = projectId;
    }
    return this.http.post<McpOAuthStartResponseDto>(
      `${this.apiUrl}/agent-credentials/mcp/${provider}/oauth/start/`,
      body,
    );
  }

  /**
   * Forgets the grant here. It does **not** revoke anything at the provider — say so
   * wherever this is offered, or someone will believe they cut off access.
   */
  public disconnectMcp(
    provider: string,
    organizationId: string,
    projectId?: string,
  ): Observable<AgentCredentialMessageResponseDto> {
    return this.http.delete<AgentCredentialMessageResponseDto>(
      `${this.apiUrl}/agent-credentials/mcp/${provider}/${organizationId}/`,
      this.scopeParams(projectId),
    );
  }
}
